import { Directive, EventEmitter, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';

import { currentResourceTab, relatedResources } from '../../components/layouts/form-view/form-view.component';
import { speedDialButtons } from '../../components/layouts/speed-dial-fab/speed-dial-fab.component';
import { ApiResponseModel } from '../../models/core/api-response.model';
import { RelatedItemModel } from '../../models/core/related-item.model';
import { OptionalType } from '../../models/types/optional.type';
import { ActionButtonsService } from '../../services/action-buttons.service';
import { ControllerService } from '../../services/controller.service';
import { ErrorsService } from '../../services/errors.service';
import { NotificationService } from '../../services/notification.service';
import { TitleService } from '../../services/title.service';
import { ButtonMapper } from '../utils/mappers/button.mapper';
import { CommandMapper } from '../utils/mappers/command.mapper';
import { Tools } from '../utils/tools';
import { BaseEntityModel } from './base-entity.model';
import { BaseEntityResource } from './base-entity.resource';
import { BaseComponent } from './base.component';

@Directive()
export abstract class BaseController<
        T extends BaseEntityModel,
        TSummary extends BaseEntityModel = T,
        TListEntityModel extends BaseEntityModel = T
    >
    extends BaseComponent
    implements OnInit, OnDestroy
{
    isEdit = false;
    isView = false;
    isCreate = false;
    shouldLoadSummary = true;
    formGroup: UntypedFormGroup;
    urlChange = new Subject<void>();
    currentActions: RelatedItemModel[];
    readonly record = signal<OptionalType<T>>(undefined);
    readonly recordId = signal<OptionalType<string>>(undefined);
    readonly recordSummary = signal<OptionalType<TSummary>>(undefined);
    readonly isLoading = signal(true);

    protected readonly _router = inject(Router);
    protected readonly _activatedRoute = inject(ActivatedRoute);
    protected readonly _controllerService = inject(ControllerService);
    protected readonly _actionButtonsService = inject(ActionButtonsService);
    protected readonly _notificationService = inject(NotificationService);
    protected readonly _errorsService = inject(ErrorsService);
    protected readonly _titleService = inject(TitleService);

    private baseButtons = [
        ButtonMapper.save.withAction(() => this.save()),
        ButtonMapper.cancel.withAction(() => (this.isEdit ? this.gotToView() : this.goToParent()))
    ];
    private errorSubscription?: Subscription;
    private currentTab?: string;

    protected abstract readonly _entityResource: BaseEntityResource<T, TSummary, TListEntityModel>;

    constructor() {
        super();

        this.addSubscriptions(this._controllerService.viewRefreshed.subscribe(() => this.setupInitialData(this._activatedRoute.snapshot)));
    }

    ngOnInit(): void {
        this.isLoading.set(true);
        this.setupInitialData(this._activatedRoute.snapshot);

        const routerSub = this._router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                distinctUntilChanged()
            )
            .subscribe(() => {
                this.isLoading.set(true);
                this.setupInitialData(this._activatedRoute.snapshot);
                this.urlChange.next();
            });

        this.addSubscriptions(routerSub);
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();

        if (!!this.errorSubscription) {
            this.errorSubscription.unsubscribe();
        }
    }

    save(): void {
        if (this.isView) {
            return;
        }

        this.record.set({
            ...(this.record() || ({} as T)),
            ...this.formGroup.value
        } as T);

        if (this.formGroup.dirty && this.formGroup.valid) {
            if (this.isEdit) {
                this._entityResource.edit(this.record()!).subscribe(r => {
                    if (!!r.value) {
                        this._notificationService.showSuccess('General.SuccessfullySaved');
                        this.gotToView();
                    }
                });
            } else {
                this._entityResource.add(this.record()!).subscribe(r => {
                    if (!!r.value) {
                        this._notificationService.showSuccess('General.SuccessfullySaved');
                        this.recordId.set(r.value.result.toString());
                        this.gotToView();
                    }
                });
            }
        } else {
            Object.keys(this.formGroup.controls).forEach(key => {
                const control = this.formGroup.controls[key];

                control.markAsDirty();
                control.markAsTouched();
                control.updateValueAndValidity();

                (control.statusChanges as EventEmitter<any>).emit(control.status);
            });
        }
    }

    goToParent(): void {
        this._router.navigate(['..'], { relativeTo: this._activatedRoute }).then();
    }

    gotToView(): void {
        this._router.navigate([this.recordId()], { relativeTo: this._activatedRoute.parent }).then();
    }

    private setupInitialData(snapshot: ActivatedRouteSnapshot): void {
        this.recordId.set(snapshot.paramMap.get('id') ?? undefined);

        this.currentTab = snapshot.paramMap.get('tab') ?? undefined;
        this.isEdit = snapshot.url.findIndex(r => r.path === 'edit') >= 0;
        this.isView = !!this.recordId() && !this.isEdit;
        this.isCreate = !(this.isEdit || this.isView);
        const currentResponse = this._controllerService.currentResponse;

        if (!this.isView) {
            this.errorSubscription = this._errorsService.onBusinessException.subscribe(r =>
                Tools.Utils.displayBusinessExceptions(this.formGroup, r)
            );
        } else if (!!this.errorSubscription) {
            this.errorSubscription.unsubscribe();
        }

        if (!!currentResponse) {
            // setTimeout is required because the content from ControllerService.execute is loaded before
            // the view. Because of that, the data will change and angular will throw an exception in dev.
            // To prevent that, we use setTimeout.
            setTimeout(() => this.loadRecord(currentResponse), 0);
        } else {
            if (this.isEdit) {
                this._entityResource.get(this.recordId()!).subscribe({
                    next: r => this.loadRecord(r),
                    error: () => this.isLoading.set(false)
                });
            } else if (this.isView) {
                if (this.shouldLoadSummary) {
                    this._entityResource.getSummary(this.recordId()!).subscribe({
                        next: r => this.loadRecord(r),
                        error: () => this.isLoading.set(false)
                    });
                } else {
                    this._entityResource.get(this.recordId()!).subscribe({
                        next: r => this.loadRecord(r),
                        error: () => this.isLoading.set(false)
                    });
                }
            } else {
                speedDialButtons.set(this.baseButtons);
                this.isLoading.set(false);
            }
        }
    }

    private loadRecord(response: ApiResponseModel<any>): void {
        this.record.set(response.value as T);
        this.recordSummary.set(response.value as TSummary);
        this._titleService.currentToolbarTitle.set(response.contentTitle);

        if (this.isView) {
            this.currentActions = response.relatedActions;

            speedDialButtons.set(this._actionButtonsService.convert(response.relatedActions));

            if (!!response.relatedResources) {
                relatedResources.set(response.relatedResources);
            }

            if (!!this.currentTab) {
                currentResourceTab.set(CommandMapper.tabs[this.currentTab]!);
            }
        } else {
            speedDialButtons.set(this.baseButtons);
        }

        this.resetFormGroup();
        this.isLoading.set(false);

        this._controllerService.currentResponse = undefined;
    }

    private resetFormGroup(): void {
        if (!!this.formGroup && !!this.record()) {
            this.formGroup.patchValue(this.record()!);
            this.formGroup.markAsPristine();
        }
    }
}
